import { DestroyRef, inject, Injectable } from '@angular/core';
import { DocumentChunkSource, PccDataSource } from '@ss/typings/nta-hunter';
import { PccDataSidePanelTypes, PccSidePanelDataMap } from '@ss/typings/pcc';
import { Observable, Subject } from 'rxjs';
import {
	PATIENT_POPOUT_PROTOCOL_VERSION,
	type PatientPopoutEndpoint,
	type PatientPopoutMessage,
	PatientPopoutMessageType,
	type PatientPopoutPostPayload,
	PatientPopoutSidePanel,
	type ShellDisconnectReason
} from './patient-popout.typings';

@Injectable({ providedIn: 'root' })
export class NtaPatientPopoutBusService {
	private channel: BroadcastChannel | null = null;

	private sessionId: string | null = null;

	private self: 'main' | 'popout' | null = null;

	private readonly incoming = new Subject<PatientPopoutMessage>();

	readonly messages$: Observable<PatientPopoutMessage> = this.incoming.asObservable();

	get connected(): boolean {
		return !!this.channel;
	}

	constructor() {
		inject(DestroyRef).onDestroy(() => this.disconnect());
	}

	popoutMessageMatchesPatient(msg: PatientPopoutMessage, patientId: string): boolean {
		const withPatientId = msg as { patientId?: string };
		if (typeof withPatientId.patientId !== 'string') return true;
		return withPatientId.patientId === patientId;
	}

	parsePatientPopoutMessage(raw: unknown): PatientPopoutMessage | null {
		if (raw === null || typeof raw !== 'object') return null;
		const data = raw as Record<string, unknown>;
		const { protocolVersion, type: messageType, from } = data;
		if (protocolVersion !== PATIENT_POPOUT_PROTOCOL_VERSION) return null;
		if (typeof messageType !== 'string') return null;
		if (from !== 'main' && from !== 'popout') return null;

		const pccDataListType = data.pccDataListType as PccDataSidePanelTypes;
		const panel = data.panel as PatientPopoutSidePanel;
		const { patientId, reason, facilityId, source, fileId, docIndex, packetId, adrDialogPacketId, page, searchQuote, pccDataItemId } = data;

		const envelope = { protocolVersion: PATIENT_POPOUT_PROTOCOL_VERSION, from: from as PatientPopoutEndpoint };
		switch (messageType) {
			case PatientPopoutMessageType.SHELL_DISCONNECTING:
				return {
					...envelope,
					type: messageType,
					...typeof reason === 'string' ? { reason: reason as ShellDisconnectReason } : {}
				};
			case PatientPopoutMessageType.SET_PATIENT:
				if (typeof patientId !== 'string' || typeof facilityId !== 'string') return null;
				return { ...envelope, type: messageType, patientId, facilityId };
			case PatientPopoutMessageType.PATIENT_READY:
				if (typeof patientId !== 'string') return null;
				return { ...envelope, type: messageType, patientId };
			case PatientPopoutMessageType.SET_SIDE_PANEL: {
				if (typeof patientId !== 'string' || !['pdf', 'generated', 'aichat', 'adr', 'pccData'].includes(panel)) return null;
				return { ...envelope, type: messageType, patientId, panel };
			}
			case PatientPopoutMessageType.VIEW_SOURCE: {
				if (typeof patientId !== 'string' || source === null || typeof source !== 'object') return null;
				const tagged = source as { sourceType?: string };
				if (tagged.sourceType === 'chunk') {
					return { ...envelope, type: messageType, patientId, source: new DocumentChunkSource(source as Partial<DocumentChunkSource>) };
				}
				if (tagged.sourceType === 'pccData') {
					return { ...envelope, type: messageType, patientId, source: new PccDataSource(source as Partial<PccDataSource>) };
				}
				return null;
			}
			case PatientPopoutMessageType.GO_TO_FILE:
				if (typeof patientId !== 'string' || typeof fileId !== 'string') return null;
				return { ...envelope, type: messageType, patientId, fileId };
			case PatientPopoutMessageType.GO_TO_GENERATED_DOCUMENT:
				if (typeof patientId !== 'string') return null;
				return {
					...envelope,
					type: messageType,
					patientId,
					...typeof fileId === 'string' ? { fileId } : {},
					...typeof docIndex === 'number' ? { docIndex } : {}
				};
			case PatientPopoutMessageType.GO_TO_ADR_PREVIEW:
				if (typeof patientId !== 'string' || typeof packetId !== 'string') return null;
				return {
					...envelope,
					type: messageType,
					patientId,
					packetId,
					...typeof page === 'number' && page >= 1 ? { page } : {},
					...typeof searchQuote === 'string' && searchQuote.trim() ? { searchQuote: searchQuote.trim() } : {}
				};
			case PatientPopoutMessageType.GO_TO_PCC_DATA_PANEL:
				if (typeof patientId !== 'string' || !(pccDataListType in PccSidePanelDataMap) || typeof pccDataItemId !== 'number' || typeof adrDialogPacketId !== 'string') return null;
				return { ...envelope, type: messageType, patientId, pccDataListType, pccDataItemId, adrDialogPacketId };
			case PatientPopoutMessageType.POPOUT_READY:
				if (typeof patientId !== 'string') return null;
				return { ...envelope, type: messageType, patientId };
			case PatientPopoutMessageType.POPOUT_CLOSED:
				return { ...envelope, type: messageType };
			default:
				return null;
		}
	}

	connect(shellSessionId: string, self: 'main' | 'popout'): void {
		if (this.sessionId === shellSessionId && this.channel && this.self === self) return;
		this.disconnect();
		this.sessionId = shellSessionId;
		this.self = self;
		const bc = new BroadcastChannel(`nta-shell-popout-${shellSessionId}`);
		this.channel = bc;
		bc.onmessage = (event: MessageEvent) => {
			const msg = this.parsePatientPopoutMessage(event.data);
			if (!msg || msg.from === self) return;
			this.incoming.next(msg);
		};
	}

	disconnect(): void {
		if (this.channel) {
			this.channel.onmessage = null;
			this.channel.close();
			this.channel = null;
		}
		this.sessionId = null;
		this.self = null;
	}

	post(payload: PatientPopoutPostPayload): void {
		if (!this.channel || !this.self) return;
		const message = { ...payload, from: this.self } as PatientPopoutMessage;
		this.channel.postMessage(message);
	}
}
